import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "mobileAppearance"; // "system" | "light" | "dark"
const DEFAULT_APPEARANCE = "light"; // first-launch default; user's explicit choice always wins once set

const applyThemeAttribute = (preference) => {
  const root = document.documentElement;
  if (preference === "light" || preference === "dark") {
    root.setAttribute("data-mobile-theme", preference);
  } else {
    root.removeAttribute("data-mobile-theme");
  }
};

// Applied as early as possible (called unconditionally at the top of
// MobileApp.jsx, before any other rendering) so the correct theme attribute
// is set before the shell's first paint — avoids a light-theme flash when
// the saved preference (or OS preference, for "system") is dark.
export const loadAndApplyStoredAppearance = () => {
  const stored = localStorage.getItem(STORAGE_KEY) || DEFAULT_APPEARANCE;
  applyThemeAttribute(stored);
  return stored;
};

const resolveMode = (preference) => {
  if (preference === "light" || preference === "dark") return preference;
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
};

export const useMobileTheme = () => {
  const [preference, setPreference] = useState(() => localStorage.getItem(STORAGE_KEY) || DEFAULT_APPEARANCE);
  const [resolvedMode, setResolvedMode] = useState(() => resolveMode(preference));

  useEffect(() => {
    applyThemeAttribute(preference);
    localStorage.setItem(STORAGE_KEY, preference);
    setResolvedMode(resolveMode(preference));
  }, [preference]);

  // "System default" must react live to an OS-level theme change while the
  // app is open, not just at startup.
  useEffect(() => {
    if (preference !== "system" || !window.matchMedia) return undefined;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      applyThemeAttribute("system");
      setResolvedMode(resolveMode("system"));
    };
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, [preference]);

  const setAppearance = useCallback((next) => setPreference(next), []);

  // resolvedMode is the actual "light" | "dark" in effect right now — what a
  // consumer like the native status bar needs, as opposed to `appearance`
  // which can be the unresolved "system" preference.
  return { appearance: preference, resolvedMode, setAppearance };
};
